import { Fragment, createElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Box, Button, Skeleton, Typography, useTheme } from '@mui/material';

import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { ListContentLayout } from '@frontend/features/common/components/ListContentLayout';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';
import { hotelInfoDefaultSourceList as defaults, HotelInfoDefaultSource } from '@frontend/features/hotel-content/constants';
import { useGetDefaultProviders, useUpdateDefaultProvidersMutation } from '@frontend/features/hotel-content/queries';
import { useHotelMetadata } from '@frontend/features/hotel-content/hooks';
import { SortableSuppliersList } from '@frontend/features/hotel-content/components/ConfigButton/SortableSuppliersList';
import { HotelDefaultProvidersDTO } from '@bandido/shared/dtos';

interface SupplierData {
  id: number;
  supplierName: string;
}

type ModerationPriority = {
  [K in HotelInfoDefaultSource]: SupplierData[];
};

export const DefaultSuppliersView = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();

  const { isLoading: loadingMeta, suppliers } = useHotelMetadata();
  const { isLoading: loadingData, data: defaultsData } = useGetDefaultProviders();

  const [supplierLists, setSupplierLists] = useState<ModerationPriority | null>();

  useEffect(() => {
    if (suppliers) {
      const buildSupplierWithOrderFromDB = (source: keyof HotelDefaultProvidersDTO): SupplierData[] => {
        const defaultIds = defaultsData?.data?.[source] || [];
  
        if (Array.isArray(defaultIds) && defaultIds.length > 0) {
          return defaultIds
            .map((id) => {
              const supplier = suppliers.find((s) => s.id === id);
              if (!supplier) return null;
              return {
                id: supplier.id,
                supplierName: supplier.supplierName,
              };
            })
            .filter((s): s is SupplierData => s !== null);
        }
  
        return suppliers.map((supplier) => ({
          id: supplier.id,
          supplierName: supplier.supplierName,
        }));
      };
  
      setSupplierLists({
        description: buildSupplierWithOrderFromDB('description'),
        location: buildSupplierWithOrderFromDB('location'),
        rooms: buildSupplierWithOrderFromDB('rooms'),
        images: buildSupplierWithOrderFromDB('images'),
        amenities: buildSupplierWithOrderFromDB('amenities'),
      });
    }
  }, [suppliers, defaultsData]);

  const { mutate: updateDefaultSuppliers } = useUpdateDefaultProvidersMutation({
    onSuccess: () => {
      addToast({
        color: 'success',
        message: t('hotels.contentManager.configuration.defaultValues.updateSuccess'),
      });
      queryClient.invalidateQueries([QueryKeys.getDefaultProviders]);
    },
    onError: () => {
      addToast({
        color: 'error',
        message: t('hotels.contentManager.configuration.defaultValues.updateError'),
      });
    },
  });

  const handleSubmit = useCallback(() => {
    const request = {
      description: supplierLists?.description.map(item => item.id),
      location: supplierLists?.location.map(item => item.id),
      rooms: supplierLists?.rooms.map(item => item.id),
      images: supplierLists?.images.map(item => item.id),
      amenities: supplierLists?.amenities.map(item => item.id),
    };
    updateDefaultSuppliers(request);
  }, [supplierLists, updateDefaultSuppliers]);

  const handleListChange = (section: HotelInfoDefaultSource, newOrder: { id: number; supplierName: string }[]) => {
    setSupplierLists(prev => {
      if (prev) {
        return {
          ...prev,
          [section]: newOrder
        };
      }

      const initialState = {
        description: [],
        location: [],
        rooms: [],
        images: [],
        amenities: [],
      };
      return {
        ...initialState,
        [section]: newOrder
      };
    });
  };

  const isLoading = loadingData || loadingMeta;

  return (
    <ListLayout title={t('hotelContent.management.tabs.defaultSuppliers')} subtitle={t('hotelContent.management.defaultSuppliers.subtitle')}>

        <Box sx={{ p: 3 }}>
          {isLoading && <Skeleton height={200} />}
          {!isLoading && supplierLists && (
            <Box>
              {defaults?.map(({ id, name, icon }) => (
                <Fragment key={id}>
                  <Box display="flex" alignItems="center" margin={theme.spacing(3, 0, 2, 0)}>
                    <Box color={theme.palette.action.active}>{createElement(icon)}</Box>
                    <Typography
                      component="span"
                      variant="h6"
                      sx={{ color: theme.palette.text.primary, marginLeft: theme.spacing(1) }}
                    >
                      {t(name)}
                    </Typography>
                  </Box>
                  <SortableSuppliersList
                    suppliers={supplierLists[id] || []}
                    onChange={(newOrder) => handleListChange(id, newOrder)}
                  />
                </Fragment>
              ))}
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" color="primary" onClick={handleSubmit}>
                  {t('common.save')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
    
    </ListLayout>
  );
};
